/**
 * Super Admin — Looker templates
 *
 * Every template in the Looker template folder: rename or re-describe it, copy
 * it (the copy stays in the folder), delete it (to Looker's Trash — restorable
 * there — and its grants go with it), make it the default, open it in Looker.
 * Below that, which workspaces have which template, one checkbox per grant.
 * The panel on /looker manages one workspace at a time; this is all of them.
 *
 * Not a security boundary — it renders for anyone who reaches the route, but
 * /api/looker-admin is behind authorizeMaster, the all-templates list needs
 * is_platform_master(), and RLS lets only a master write grants or the
 * default. A non-master reads and changes nothing.
 *
 * Making a template the default is a direct table write and is not audited;
 * rename, copy and delete are (audit_logs, entity type looker_template).
 *
 * "New template" and a template's "Edit tiles" open the tile builder
 * (LookerTemplateBuilder); only templates made there carry "Edit tiles".
 *
 * @module pages/admin/LookerTemplates
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { YDMPageHero } from '../../components/brand';
import { LookerTemplateTable } from '@/components/looker/admin/LookerTemplateTable';
import { LookerTemplateMatrix } from '@/components/looker/admin/LookerTemplateMatrix';
import { listWorkspaces, type AdminWorkspace } from '@/services/adminService';
import {
  copyLookerTemplate, deleteLookerTemplate, fetchLookerTemplates, renameLookerTemplate,
} from '@/services/looker/lookerApi';
import {
  fetchAllTemplateGrants, grantKey, grantLookerTemplate, revokeLookerTemplate, setDefaultLookerTemplate,
  type TemplateGrant,
} from '@/services/looker/lookerTemplateAccess';
import type { LookerTemplateSummary } from '@/services/looker/lookerTypes';

type AdminError =
  | 'load' | 'workspaces' | 'grants' | 'notConnected' | 'forbidden' | 'notFound'
  | 'isDefault' | 'rateLimited' | 'unavailable' | 'saveFailed';

/** What an /api/looker-* status means to the master; 0 is a network failure. */
const errorForStatus = (status: number, fallback: AdminError): AdminError => {
  if (status === 401 || status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status === 409) return 'isDefault';
  if (status === 429) return 'rateLimited';
  if (status === 503) return 'notConnected';
  if (status === 502 || status === 0) return 'unavailable';
  return fallback;
};

const SECTION = 'rounded-lg border border-neutral-200 bg-white';

const AdminLookerTemplates: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<LookerTemplateSummary[] | null>(null);
  const [workspaces, setWorkspaces] = useState<AdminWorkspace[] | null>(null);
  const [grants, setGrants] = useState<TemplateGrant[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<AdminError | null>(null);

  // Literal keys, so the locale integrity test sees every one of them.
  const errorText: Record<AdminError, string> = {
    load: t('looker.admin.errors.load'),
    workspaces: t('looker.admin.errors.workspaces'),
    grants: t('looker.admin.errors.grants'),
    notConnected: t('looker.admin.errors.notConnected'),
    forbidden: t('looker.admin.errors.forbidden'),
    notFound: t('looker.admin.errors.notFound'),
    isDefault: t('looker.admin.errors.isDefault'),
    rateLimited: t('looker.admin.errors.rateLimited'),
    unavailable: t('looker.admin.errors.unavailable'),
    saveFailed: t('looker.admin.errors.saveFailed'),
  };

  /** Reads all three again. Leaves an earlier write's error alone unless a read fails. */
  const load = useCallback(async () => {
    const [tpl, ws, gr] = await Promise.all([fetchLookerTemplates({ kind: 'all' }), listWorkspaces(), fetchAllTemplateGrants()]);
    setTemplates(tpl.ok ? tpl.data : null);
    setWorkspaces(ws.success ? ws.data ?? [] : null);
    setGrants(gr.ok ? gr.data : null);
    if (!tpl.ok) setError(errorForStatus(tpl.status, 'load'));
    else if (!ws.success) setError('workspaces');
    else if (!gr.ok) setError('grants');
  }, []);

  useEffect(() => {
    void load().finally(() => setLoading(false));
  }, [load]);

  /** One write at a time; a failure stays on screen, a success refetches everything. */
  const run = async (write: () => Promise<AdminError | null>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    const failed = await write();
    if (failed) setError(failed);
    else await load();
    setBusy(false);
    return failed === null;
  };

  const viaApi = async (call: Promise<{ ok: true } | { ok: false; status: number }>): Promise<AdminError | null> => {
    const r = await call;
    return r.ok ? null : errorForStatus(r.status, 'saveFailed');
  };
  const viaTable = async (call: Promise<boolean>): Promise<AdminError | null> => ((await call) ? null : 'saveFailed');

  const grantCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const g of grants ?? []) counts.set(g.templateId, (counts.get(g.templateId) ?? 0) + 1);
    return counts;
  }, [grants]);
  const granted = useMemo(() => new Set((grants ?? []).map((g) => grantKey(g.workspaceId, g.templateId))), [grants]);

  return (
    <div className="space-y-4 animate-fade-in">
      <YDMPageHero eyebrow="Super Admin" headline={t('looker.admin.headline')} subtitle={t('looker.admin.subtitle')} />

      {error && (
        <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {errorText[error]}
        </p>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-neutral-800" role="status">
          <Loader2 className="h-6 w-6 animate-spin text-ink-900" aria-hidden />
          {t('looker.admin.loading')}
        </div>
      ) : (
        templates && (
          <>
            <section className={`${SECTION} overflow-x-auto`} aria-label={t('looker.admin.listTitle')}>
              <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-4">
                <h2 className="text-base font-semibold text-ink-900">{t('looker.admin.listTitle')}</h2>
                <button
                  type="button"
                  className="inline-flex h-8 items-center rounded-md bg-primary-600 px-3 text-sm font-medium text-ink-950 hover:bg-primary-700"
                  onClick={() => navigate('/admin/looker-templates/new')}
                >
                  {t('looker.admin.newTemplate')}
                </button>
              </div>
              <LookerTemplateTable
                templates={templates}
                grantCounts={grantCounts}
                busy={busy}
                onRename={(id, title, description) => run(() => viaApi(renameLookerTemplate(id, title, description)))}
                onCopy={(id) => void run(() => viaApi(copyLookerTemplate(id)))}
                onEditTiles={(id) => navigate(`/admin/looker-templates/${id}/edit`)}
                onDelete={(id) => run(() => viaApi(deleteLookerTemplate(id)))}
                onMakeDefault={(id) => void run(() => viaTable(setDefaultLookerTemplate(id)))}
              />
            </section>

            {workspaces && grants && (
              <section className={`${SECTION} overflow-x-auto`} aria-label={t('looker.admin.matrixTitle')}>
                <h2 className="px-4 pt-4 text-base font-semibold text-ink-900">{t('looker.admin.matrixTitle')}</h2>
                <p className="px-4 pt-1 text-sm text-neutral-800">{t('looker.admin.matrixHint')}</p>
                <LookerTemplateMatrix
                  templates={templates}
                  workspaces={workspaces}
                  granted={granted}
                  busy={busy}
                  onToggle={(ws, id, on) =>
                    void run(() => viaTable(on ? grantLookerTemplate(ws, id) : revokeLookerTemplate(ws, id)))}
                />
              </section>
            )}
          </>
        )
      )}
    </div>
  );
};

export default AdminLookerTemplates;
